<script lang="ts">
import { getMetadataText } from '@@/bits/attachments'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { isPhotoAlbum } from '@@/bits/post_attachment'
import { getPostPropertyFormattedValue } from '@@/bits/post_properties'
import { isPostPinned } from '@@/bits/post_state'
import { hasPollAttachment } from '@@/bits/surface_polls'
import { WallWishGroupByTypes } from '@@/enums'
import OzBaseButton, { OzBaseButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
import type { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzBox from '@@/library/v4/components/OzBox.vue'
import OzDivider, { OzDividerColorScheme, OzDividerOrientation } from '@@/library/v4/components/OzDivider.vue'
import OzDropIndicatorLine from '@@/library/v4/components/OzDropIndicatorLine.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { LogicalEdge, OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import type { Cid, Post, WallCustomPostProperty } from '@@/types'
import SurfaceAttachmentPreview from '@@/vuecomponents/SurfaceAttachmentPreview.vue'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfacePostDragPreview from '@@/vuecomponents/SurfacePostDragPreview.vue'
import { useSurfacePostDraggableHelper } from '@@/vuecomposables/surface_post_draggable_helper'
import { usePostSorter } from '@@/vuecomposables/surface_post_sorter'
import { asyncComputed } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

export const SURFACE_MAP_POST_MANUAL_DRAG_PREVIEW_ID = 'surface-map-post-manual-drag-preview'
export const SURFACE_MAP_POST_LIST_ITEM_DRAG_TYPE = 'surface-map-post-list-item'
</script>

<script setup lang="ts">
const props = defineProps<{
  groupId: number | string
  post: Post
  boxColor?: OzBoxColors
  borderRadius?: 0 | 12
  xDivider?: boolean
  fontClass?: string
  axis: 'vertical' | 'horizontal'
  flexDirection: 'row' | 'column'
  dropIndicatorGap: number
  dropIndicatorClass: string[]
}>()

const emit = defineEmits<{
  (name: 'post-click', postCid: Cid): void
  (name: 'post-dbl-click', postCid: Cid, authorId: number): void
  (name: 'post-hover', postCid: Cid): void
  (name: 'post-unhover', postCid: Cid): void
  // Drag and drop for post
  (name: 'on-drag-start'): void
  (name: 'on-drop'): void
}>()

const { backgroundLuminance, colorScheme } = storeToRefs(useSurfaceStore())
const { postListPosition } = storeToRefs(useSurfaceMapStore())
const { getLinkDisplayAttributesForUrl } = useSurfaceAttachmentsStore()
const { wallCustomPostProperties } = storeToRefs(useSurfacePostPropertiesStore())
const { isAPostDragging, groupIdUnderPostBeingDragged, shouldDisableDraggingPost } = storeToRefs(
  useSurfacePostsDragAndDropStore(),
)
const surfacePostsStore = useSurfacePostsStore()
const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
const { wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())

// Strip all tags because we don't want any styles in the post list
const postBody = computed(() => props.post.body ?? '')
const linkDisplayAttributes = computed(() => {
  return props.post.attachment
    ? getLinkDisplayAttributesForUrl(props.post.attachment, props.post.attachment_link)
    : null
})
const linkMetadataText = computed(() =>
  linkDisplayAttributes.value ? getMetadataText(linkDisplayAttributes.value) : '',
)
const xPin = computed(() => isPostPinned(props.post))
const isDarkMode = computed(() => colorScheme.value === 'dark')

const postHasPoll = computed(() => hasPollAttachment(props.post))

const firstPostCustomPropertyValue = asyncComputed<string>(async () => {
  const postCustomPropertyValues = props.post.custom_properties ?? {}
  if (Object.values(postCustomPropertyValues).length === 0) return ''

  const firstPopulatedWallCustomProperty = wallCustomPostProperties.value
    .filter((property: WallCustomPostProperty) => property.id != null)
    .find((property: WallCustomPostProperty) => postCustomPropertyValues[property.id as string] != null)
  if (firstPopulatedWallCustomProperty == null) return ''

  const firstPopulatedCustomPropertyValue = postCustomPropertyValues[firstPopulatedWallCustomProperty.id as string]
  if (firstPopulatedCustomPropertyValue == null) return ''

  return await getPostPropertyFormattedValue({
    property: firstPopulatedWallCustomProperty,
    value: firstPopulatedCustomPropertyValue,
  })
}, '')

const minPostHeight = 54 // OzBox with only the subject has a minimum height of 54px
const minPostWidth = 304 // SurfaceMapPostListHorizontal.vue has a minimum width of 304px

const isPhotoAlbumAttachment = computed(() => isAppUsing('photoAlbum') && isPhotoAlbum(props.post))
const numAdditionalImagesInPhotoAlbum = computed(() => {
  if (!isPhotoAlbumAttachment.value || !props.post.attachments) return 0
  return props.post.attachments.length - 1
})

// #region Drag and drop post

function handlePostDragStart(): void {
  isAPostDragging.value = true
  groupIdUnderPostBeingDragged.value = props.groupId
}
function handlePostDrop(e: OnDropPayload<{ post: Post }, { post: Post }>): void {
  isAPostDragging.value = false
  groupIdUnderPostBeingDragged.value = undefined
  const targetPost = e.targetItem.data.post
  const sourcePost = e.sourceItem.data.post
  const posts = allSortedPostsByGroup.value[props.groupId]
  if (!posts) return
  const targetIndex = posts.findIndex((post) => post.cid === targetPost.cid)
  // Early return if the target post is not in the group
  if (targetIndex === -1) return
  let prevPost: Post | undefined
  let nextPost: Post | undefined
  if (e.relativePositionToTarget === 'before') {
    prevPost = posts[targetIndex - 1]
    nextPost = posts[targetIndex]
  } else {
    prevPost = posts[targetIndex]
    nextPost = posts[targetIndex + 1]
  }

  if (isPostPinned(sourcePost)) {
    // Dropping a pinned post before an unpinned post -> don't take next unpinned post into account
    if (!isPostPinned(nextPost)) {
      nextPost = undefined
    }
  } else {
    // Dropping an unpinned post after a pinned post -> don't take previous pinned post into account
    if (isPostPinned(prevPost)) {
      prevPost = undefined
    }
  }

  const { sortIndex, gapSize } = usePostSorter().getNewIndexBetween(prevPost, nextPost)
  // Given the groupId is a string, we need to convert it to a number. We fallback to null if it's not a number.
  let sectionId = wishGroupBy.value === WallWishGroupByTypes.Section ? Number(props.groupId) : null
  if (Number.isNaN(sectionId)) {
    sectionId = null
  }

  // Update new post order to server
  surfacePostsStore.sortPost({
    postCid: sourcePost.cid,
    sortIndex,
    sectionId,
    gapSize,
  })

  // Scroll the source post into view after dropping
  // Add setTimeout to ensure the DOM is updated
  setTimeout(() => {
    const element = document.querySelector(`[data-id="${sourcePost.id}"]`)
    element?.scrollIntoView({ behavior: 'instant', block: 'center' })
  }, 0)
}

const itemData = computed<{ post: Post; width?: number }>(() => ({
  post: props.post,
  width: 290,
}))
const elementRef = ref<HTMLElement | undefined>(undefined)
const { isDragging } = useDraggableElement({
  elementRef,
  type: SURFACE_MAP_POST_LIST_ITEM_DRAG_TYPE,
  data: itemData,
  dragPreview: SurfacePostDragPreview,
  dragPreviewComponentProps: itemData,
  isDraggable: computed(() => !shouldDisableDraggingPost.value),
  skipSafariPatch: device.ios,
  onDragStart: (e) => {
    handlePostDragStart()
    emit('on-drag-start')
  },
})

// #endregion

// #region Making post droppable
const fixedEdge = ref<LogicalEdge | undefined>(undefined)
const { closestEdgeToPointer, addDraggableSource } = useDropTargetElement({
  elementRef,
  type: SURFACE_MAP_POST_LIST_ITEM_DRAG_TYPE,
  data: itemData,
})

const { canDropPost, getEdgeForDropTargetPost } = useSurfacePostDraggableHelper()
addDraggableSource<{ post: Post }>({
  type: SURFACE_MAP_POST_LIST_ITEM_DRAG_TYPE,
  axis: props.axis,
  onDrag: (e) => {
    const dropTargetPost = (e.self.data.data as { post: Post }).post
    const sourcePost = (e.source.data.data as { post: Post }).post
    fixedEdge.value = getEdgeForDropTargetPost(dropTargetPost, sourcePost, props.axis)
  },
  onDragLeave: () => {
    fixedEdge.value = undefined
  },
  canDrop: (e) => {
    const draggedPost = e.sourceItem.data.post
    const targetPost = e.targetItem.data.post
    return canDropPost(draggedPost, targetPost)
  },
  onDrop: (e) => {
    handlePostDrop(e)
    emit('on-drop')
    fixedEdge.value = undefined
  },
})
const actualEdge = computed(() => fixedEdge.value ?? closestEdgeToPointer.value)
// #endregion

function onDragEnd(): void {
  // If the post is not being dragged, we don't need to remove the drag preview element.
  if (!isAPostDragging.value) return

  // Manually reset the drag and drop state.
  isAPostDragging.value = false
  groupIdUnderPostBeingDragged.value = undefined
}

onMounted(() => {
  if (!elementRef.value) return
  elementRef.value.addEventListener('dragend', onDragEnd)
})

onBeforeUnmount(() => {
  if (!elementRef.value) return
  elementRef.value.removeEventListener('dragend', onDragEnd)
})
</script>

<template>
  <div ref="elementRef" class="relative">
    <OzBox
      :data-post-cid="post.cid"
      :data-testid="`map-post-list-item-${post.id}`"
      :data-rank="post.sort_index"
      :class="[
        'overflow-hidden',
        {
          'p-0': postListPosition === 'bottom',
          'px-2 pt-1': postListPosition === 'side',
        },
        'flex',
        flexDirection === 'column' && 'flex-col',
        flexDirection === 'row' && 'flex-row',
      ]"
      :color="boxColor"
      :dark-mode="isDarkMode"
      :radius="borderRadius"
    >
      <OzBaseButton
        :class="[
          'bg-transparent w-full group p-0',
          postListPosition === 'bottom' && 'h-full relative flex items-center justify-center',
          isDragging && 'opacity-40',
        ]"
        :href-mode="OzBaseButtonHrefMode.DisplayOnly"
        :data-bg-luminance="backgroundLuminance"
        :aria-label="__('Reveal this post in the map')"
        :aria-describedby="`${post.cid}-postDescription`"
        @click="$emit('post-click', post.cid)"
        @dblclick="$emit('post-dbl-click', post.cid, Number(post.author_id))"
        @keydown.enter="$emit('post-click', post.cid)"
        @focus="$emit('post-hover', post.cid)"
        @mouseover="$emit('post-hover', post.cid)"
        @blur="$emit('post-unhover', post.cid)"
        @mouseleave="$emit('post-unhover', post.cid)"
      >
        <SurfaceLazy
          tag="span"
          :min-height="minPostHeight"
          :min-width="minPostWidth"
          :render-immediately="!isAppUsing('lazyRenderPosts')"
        >
          <!-- if post list position is bottom, we want the content to take up a base height of 60px, and only take more space as needed if the content is longer/ has attachment -->
          <!-- to ensure focus visible style looks the same regardless of the content height, we create an absolutely positioned container for showing focus-visible styles -->
          <span
            v-if="postListPosition === 'bottom'"
            :class="[
              'absolute w-[calc(100%-1rem)] h-[calc(100%-1rem)] p-2 rounded-xl box-border z-surface-map-control',
              {
                'group-focus-visible:ring-2': true,
                'group-focus-visible:ring-canary-500': isDarkMode,
                'group-focus-visible:ring-grape-500': !isDarkMode,
              },
            ]"
          />
          <span
            :class="[
              {
                'flex items-center max-w-full text-start': true,
                'hover-hover:hover:bg-overlay-text-bg-light-mode-light-hover': backgroundLuminance === 'light',
                'hover-hover:hover:bg-overlay-text-bg-dark-mode-dark-hover': backgroundLuminance === 'dark',
                'cursor-pointer': true,
                'wish-list-item': true,
              },
              postListPosition === 'side' && {
                'rounded-xl mb-1': true,
                'h-auto': true,
                'px-2 py-3': true,
                'group-focus-visible:ring-2': true,
                'group-focus-visible:ring-canary-500': isDarkMode,
                'group-focus-visible:ring-grape-500': !isDarkMode,
              },
              postListPosition === 'bottom' && {
                'ps-4 pe-3 py-5': true,
                'h-[60px] box-content': true,
              },
              fontClass,
            ]"
          >
            <span :class="['grow flex max-w-full']">
              <span
                :class="[
                  'grow',
                  'max-w-full',
                  'flex flex-col items-start',
                  {
                    'space-y-0': postListPosition === 'bottom',
                    'space-y-1': postListPosition === 'side',
                  },
                  {
                    'w-50': postListPosition === 'bottom',
                    'w-auto': postListPosition === 'side',
                  },
                  'overflow-hidden',
                ]"
              >
                <span
                  v-if="xPin || post.location_name"
                  :id="`${post.cid}-postDescription`"
                  :class="['flex w-full items-center', 'space-s-2']"
                >
                  <OzIcon
                    v-if="xPin"
                    :class="[colorScheme === 'light' && 'text-grape-500', colorScheme === 'dark' && 'text-canary-500']"
                    name="pin_filled"
                    :size="16"
                  />
                  <span v-if="xPin" class="sr-only">{{ __('Pinned post') }}</span>

                  <span
                    v-if="post.location_name"
                    :class="{
                      'text-body-extra-small truncate': true,
                      'text-dark-text-200': colorScheme === 'light',
                      'text-light-text-200': colorScheme === 'dark',
                    }"
                  >
                    {{ post.location_name }}
                  </span>
                </span>

                <span
                  v-if="post.subject"
                  :class="{
                    'text-17-21': postListPosition === 'bottom',
                    'text-body-small': postListPosition === 'side',
                    'line-clamp-2': postListPosition === 'bottom',
                    'line-clamp-1': postListPosition === 'side',
                    'break-word-anywhere': true,
                    'whitespace-break-spaces': true,
                    'text-dark-text-100': colorScheme === 'light',
                    'text-light-text-100': colorScheme === 'dark',
                  }"
                  v-text="post.subject"
                />

                <!-- Fallback when location and subject are both absent -->
                <template v-if="!post.subject && !post.location_name">
                  <span
                    v-if="postHasPoll"
                    :class="{
                      'text-17-21': postListPosition === 'bottom',
                      'text-body-small': postListPosition === 'side',
                      'line-clamp-2': postListPosition === 'bottom',
                      'line-clamp-1': postListPosition === 'side',
                      'break-word-anywhere': true,
                      'text-dark-text-100': colorScheme === 'light',
                      'text-light-text-100': colorScheme === 'dark',
                    }"
                  >
                    {{ __('Poll') }}
                  </span>
                  <span
                    v-else-if="isPhotoAlbumAttachment"
                    :class="{
                      'text-17-21': postListPosition === 'bottom',
                      'text-body-small': postListPosition === 'side',
                      'line-clamp-2': postListPosition === 'bottom',
                      'line-clamp-1': postListPosition === 'side',
                      'break-word-anywhere': true,
                      'text-dark-text-100': colorScheme === 'light',
                      'text-light-text-100': colorScheme === 'dark',
                    }"
                  >
                    {{ __('Photo album') }}
                  </span>
                  <span
                    v-else-if="postBody"
                    :class="{
                      'text-17-21': postListPosition === 'bottom',
                      'text-body-small': postListPosition === 'side',
                      'line-clamp-2': postListPosition === 'bottom',
                      'line-clamp-1': postListPosition === 'side',
                      'break-word-anywhere': true,
                      'whitespace-break-spaces': true,
                      'text-dark-text-100': colorScheme === 'light',
                      'text-light-text-100': colorScheme === 'dark',
                    }"
                    v-html="postBody"
                  />
                  <!-- Neither subject nor body - there should be an attachment so we display metadata text instead -->
                  <span
                    v-else-if="linkMetadataText"
                    :class="{
                      'text-17-21': postListPosition === 'bottom',
                      'text-body-small': postListPosition === 'side',
                      'line-clamp-2': postListPosition === 'bottom',
                      'line-clamp-1': postListPosition === 'side',
                      'break-word-anywhere': true,
                      'text-dark-text-100': colorScheme === 'light',
                      'text-light-text-100': colorScheme === 'dark',
                    }"
                  >
                    {{ linkMetadataText }}
                  </span>

                  <span
                    v-else
                    :class="{
                      'text-17-21': postListPosition === 'bottom',
                      'text-body-small': postListPosition === 'side',
                      'line-clamp-2': postListPosition === 'bottom',
                      'line-clamp-1': postListPosition === 'side',
                      'break-word-anywhere': true,
                      'text-dark-text-100': colorScheme === 'light',
                      'text-light-text-100': colorScheme === 'dark',
                    }"
                  >
                    {{ firstPostCustomPropertyValue }}
                  </span>
                </template>
              </span>
              <span
                :class="[
                  'shrink-0 flex items-center',
                  {
                    'ms-5': postListPosition === 'bottom',
                    'ms-2': postListPosition === 'side',
                  },
                  {
                    'w-14': postListPosition === 'bottom',
                    'w-10': postListPosition === 'side',
                  },
                ]"
              >
                <span
                  v-if="postHasPoll"
                  :class="[
                    'flex items-center justify-center',
                    'rounded-md',
                    'w-full',
                    {
                      'h-14': postListPosition === 'bottom',
                      'h-10': postListPosition === 'side',
                    },
                  ]"
                >
                  <OzIcon class="text-park-500" :width="postListPosition === 'bottom' ? 24 : 20" name="poll" />
                </span>
                <span
                  v-else-if="post.attachment"
                  :class="[
                    'overflow-hidden relative',
                    'rounded-md',
                    'block',
                    'w-full',
                    {
                      'h-14': postListPosition === 'bottom',
                      'h-10': postListPosition === 'side',
                    },
                  ]"
                >
                  <SurfaceAttachmentPreview
                    :post-cid="post.cid"
                    :url="post.attachment"
                    :width="postListPosition === 'bottom' ? 56 : 40"
                    :height="postListPosition === 'bottom' ? 56 : 40"
                    :aspect-ratio="1"
                    usage-context="postPreview"
                    :attachment-link="post.attachment_link"
                    :custom-thumbnail-url="post.wish_content?.attachment_props?.custom_thumbnail_url"
                    :retain-transparent-image-size="true"
                  />
                  <div v-if="numAdditionalImagesInPhotoAlbum > 0" class="rounded-md overflow-hidden absolute inset-0">
                    <div :class="['z-10 w-full h-full grid place-items-center backdrop-blur-md rounded-md']">
                      <span
                        :class="[
                          'flex items-center text-body font-semibold',
                          isDarkMode ? 'text-dark-text-100' : 'text-light-text-100',
                        ]"
                        aria-hidden="true"
                        ><span>+</span><span>{{ numAdditionalImagesInPhotoAlbum }}</span></span
                      >
                    </div>
                  </div>
                </span>
              </span>
            </span>
          </span>
        </SurfaceLazy>
      </OzBaseButton>
      <template v-if="xDivider">
        <OzDivider
          :class="[postListPosition === 'side' && 'mx-2', 'wish-list-item-divider']"
          :orientation="postListPosition === 'bottom' ? OzDividerOrientation.Vertical : OzDividerOrientation.Horizontal"
          :dark-mode="colorScheme === 'dark'"
          :color-scheme="OzDividerColorScheme.Solid"
        />
      </template>
    </OzBox>
    <OzDropIndicatorLine
      v-if="actualEdge"
      :edge="actualEdge"
      :gap="dropIndicatorGap"
      :dark-mode="isDarkMode"
      :class="dropIndicatorClass"
    />
  </div>
</template>
